/**
 * Seller submission admin inbox UI constants (M10).
 */
import {
  SELLER_ADMIN_REVIEW_STATUSES,
  SELLER_OWNERSHIP_STATUSES,
  SELLER_SUBMISSION_STATUSES,
  SELLER_TYPES,
  SELLER_WORKFLOW_STATUSES,
} from "../utils/sellerIntake/sellerIntakeConstants";

export const SELLER_SUBMISSION_ADMIN_PATH = "/admin/seller-submissions";

export const SELLER_REVIEW_STATUS_LABELS_ES = Object.freeze({
  [SELLER_ADMIN_REVIEW_STATUSES.NEW]: "Nuevo",
  [SELLER_ADMIN_REVIEW_STATUSES.REVIEWED]: "Revisado",
  [SELLER_ADMIN_REVIEW_STATUSES.NEEDS_INFO]: "Necesita información",
  [SELLER_ADMIN_REVIEW_STATUSES.CONTACTED]: "Contactado",
  [SELLER_ADMIN_REVIEW_STATUSES.APPROVED]: "Aprobado",
  [SELLER_ADMIN_REVIEW_STATUSES.REJECTED]: "Rechazado",
  [SELLER_ADMIN_REVIEW_STATUSES.CONVERTED_TO_LISTING]: "Convertido a publicación",
  [SELLER_ADMIN_REVIEW_STATUSES.ARCHIVED]: "Archivado",
  [SELLER_ADMIN_REVIEW_STATUSES.SPAM]: "Spam",
});

export const SELLER_WORKFLOW_STATUS_LABELS_ES = Object.freeze({
  [SELLER_WORKFLOW_STATUSES.NOT_STARTED]: "Sin iniciar",
  [SELLER_WORKFLOW_STATUSES.IN_REVIEW]: "En revisión",
  [SELLER_WORKFLOW_STATUSES.WAITING_ON_SELLER]: "Esperando vendedor",
  [SELLER_WORKFLOW_STATUSES.READY_FOR_LISTING]: "Listo para publicación",
  [SELLER_WORKFLOW_STATUSES.CONVERTED]: "Convertido",
  [SELLER_WORKFLOW_STATUSES.CLOSED]: "Cerrado",
});

export const SELLER_SUBMISSION_STATUS_LABELS_ES = Object.freeze({
  [SELLER_SUBMISSION_STATUSES.RECEIVED]: "Recibido",
});

export const SELLER_SPAM_STATUS_LABELS_ES = Object.freeze({
  none: "Ninguno",
  spam: "Spam",
  suspected: "Sospechoso",
});

export const SELLER_TYPE_LABELS_ES = Object.freeze({
  [SELLER_TYPES.INDIVIDUAL]: "Persona natural",
  [SELLER_TYPES.DEALER]: "Concesionario",
  [SELLER_TYPES.SMALL_LOT]: "Lote independiente",
  [SELLER_TYPES.UNKNOWN]: "Desconocido",
});

export const SELLER_OWNERSHIP_LABELS_ES = Object.freeze({
  [SELLER_OWNERSHIP_STATUSES.OWNER]: "Dueño",
  [SELLER_OWNERSHIP_STATUSES.DEALER_INVENTORY]: "Inventario concesionario",
  [SELLER_OWNERSHIP_STATUSES.CONSIGNMENT]: "Consignación",
  [SELLER_OWNERSHIP_STATUSES.UNKNOWN]: "Otro",
});

export function formatSellerReviewStatus(status) {
  return SELLER_REVIEW_STATUS_LABELS_ES[status] || status || "—";
}

export function formatSellerWorkflowStatus(status) {
  return SELLER_WORKFLOW_STATUS_LABELS_ES[status] || status || "—";
}

export function formatSellerSubmissionStatus(status) {
  return SELLER_SUBMISSION_STATUS_LABELS_ES[status] || status || "—";
}

export function formatSellerSpamStatus(status) {
  return SELLER_SPAM_STATUS_LABELS_ES[status] || status || "—";
}

export function formatSellerType(type) {
  return SELLER_TYPE_LABELS_ES[type] || type || "—";
}

export function sellerReviewStatusBadgeClass(status) {
  if (status === "new") return "lead-badge lead-badge--new";
  if (status === "needs_info") return "lead-badge lead-badge--warning";
  if (status === "approved" || status === "converted_to_listing") return "lead-badge lead-badge--success";
  if (status === "rejected" || status === "spam") return "lead-badge lead-badge--danger";
  if (status === "contacted" || status === "reviewed") return "lead-badge lead-badge--info";
  if (status === "archived") return "lead-badge lead-badge--muted";
  return "lead-badge";
}

export function sellerWorkflowStatusBadgeClass(status) {
  if (status === "ready_for_listing") return "lead-badge lead-badge--success";
  if (status === "waiting_on_seller") return "lead-badge lead-badge--warning";
  if (status === "in_review") return "lead-badge lead-badge--info";
  if (status === "closed") return "lead-badge lead-badge--muted";
  return "lead-badge";
}

export const SELLER_WORKFLOW_ACTIONS = Object.freeze({
  MARK_REVIEWED: "mark_reviewed",
  MARK_NEEDS_INFO: "mark_needs_info",
  MARK_CONTACTED: "mark_contacted",
  APPROVE: "approve",
  REJECT: "reject",
  ARCHIVE: "archive",
  MARK_SPAM: "mark_spam",
  REOPEN: "reopen",
});

export const SELLER_WORKFLOW_ACTION_LABELS_ES = Object.freeze({
  mark_reviewed: "Marcar revisado",
  mark_needs_info: "Necesita información",
  mark_contacted: "Marcar contactado",
  approve: "Aprobar",
  reject: "Rechazar",
  archive: "Archivar",
  mark_spam: "Marcar spam",
  reopen: "Reabrir",
});

export function formatVehicleSummary(submission = {}) {
  const parts = [
    submission.vehicleYear,
    submission.vehicleMake,
    submission.vehicleModel,
    submission.vehicleTrim,
  ].filter(Boolean);
  return parts.join(" ") || "—";
}

export function formatCopPrice(value, currency = "COP") {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  if (currency === "COP") {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return `${amount} ${currency}`;
}

export function formatAdminDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export function formatLocation(submission = {}) {
  return [submission.city, submission.department].filter(Boolean).join(", ") || "—";
}

export const SELLER_CONTACT_STATUS_LABELS_ES = Object.freeze({
  not_contacted: "No contactado",
  contact_attempted: "Intento de contacto",
  contacted: "Contactado",
  needs_follow_up: "Requiere seguimiento",
  waiting_on_seller: "Esperando vendedor",
  seller_unresponsive: "Sin respuesta",
  seller_declined: "Vendedor desistió",
  ready_to_publish: "Listo para publicar",
  closed: "Cerrado",
});

export const SELLER_CONTACT_METHOD_LABELS_ES = Object.freeze({
  whatsapp: "WhatsApp",
  phone: "Llamada",
  email: "Correo",
  other: "Otro",
});

export const SELLER_FOLLOW_UP_PRIORITY_LABELS_ES = Object.freeze({
  low: "Baja",
  normal: "Normal",
  high: "Alta",
});

export const SELLER_COMMUNICATION_QUICK_ACTIONS = Object.freeze([
  { status: "contact_attempted", label: "Intento de contacto" },
  { status: "contacted", label: "Marcar contactado" },
  { status: "waiting_on_seller", label: "Esperando vendedor" },
  { status: "needs_follow_up", label: "Requiere seguimiento" },
  { status: "seller_unresponsive", label: "Sin respuesta" },
  { status: "seller_declined", label: "Vendedor desistió" },
]);

export function formatSellerContactStatus(status) {
  return SELLER_CONTACT_STATUS_LABELS_ES[status] || status || "—";
}

export function formatSellerContactMethod(method) {
  return SELLER_CONTACT_METHOD_LABELS_ES[method] || method || "—";
}

export function formatSellerFollowUpPriority(priority) {
  return SELLER_FOLLOW_UP_PRIORITY_LABELS_ES[priority] || priority || "—";
}

export function sellerContactStatusBadgeClass(status) {
  if (status === "not_contacted") return "lead-badge";
  if (status === "contact_attempted" || status === "needs_follow_up") return "lead-badge lead-badge--warning";
  if (status === "contacted" || status === "ready_to_publish") return "lead-badge lead-badge--success";
  if (status === "waiting_on_seller") return "lead-badge lead-badge--info";
  if (status === "seller_unresponsive" || status === "seller_declined" || status === "closed") {
    return "lead-badge lead-badge--muted";
  }
  return "lead-badge";
}
